// @ts-expect-error no type declarations for superagent-promise
import superagentPromise from 'superagent-promise';
// @ts-expect-error no type declarations for superagent
import _superagent from 'superagent';
import { HttpError } from '@shared/models/error.models';
import { apiUrl } from './EnvironmentVariables';
import { resetAllLoginStorages } from '@shared/utils/AuthUtils';
import { TrackJS } from 'trackjs';
import { getMsalTokens, isMsalAuth } from './tokenProvider';
import { getAuthHeaders, refreshMsalToken, AuthHeaders } from './tokenService';
import { getActiveGateway } from '@shared/auth/AuthGatewayProvider';

const superagent = superagentPromise(_superagent, Promise);

const API_ROOT = apiUrl;

function applyHeaders(req: any, headers: AuthHeaders) {
  for (const [key, value] of Object.entries(headers)) {
    if (value != null) {
      req.set(key, value);
    }
  }
}

const responseBody = (res: any) => {
  // MSAL manages MS tokens — skip localStorage writes for MS auth.
  // Google/Standard still need server-sent token refresh via response headers.
  if (isMsalAuth()) {
    return res.body;
  }

  if (!!res.headers.accessToken && localStorage.getItem('accessToken') !== res.headers.accessToken) {
    localStorage.setItem('accessToken', res.headers.accessToken);
  }
  if (!!res.headers.idToken && localStorage.getItem('idToken') !== res.headers.idToken) {
    localStorage.setItem('idToken', res.headers.idToken);
  }
  return res.body;
};

function isRequestTerminated(err: any): boolean {
  return err && !err.response && err.message &&
    err.message.includes('Request has been terminated');
}

function hasTokensAvailable(): boolean {
  if (isMsalAuth()) {
    return true; // tokens are in MSAL cache, not localStorage
  }
  return !!localStorage.getItem('accessToken');
}

async function processError(err: any, passive: boolean, retryFn?: () => Promise<any>): Promise<any> {
  if (isRequestTerminated(err)) {
    TrackJS?.track(`Request terminated: visibilityState=${document.visibilityState}, authType=${localStorage.getItem('type') || 'none'}, hasTokens=${hasTokensAvailable()}`);
    return;
  }
  if (err && err.response) {
    // A flagged-for-deletion user attempted to authenticate — route them to the
    // dedicated "account deleted" page rather than the generic login flow.
    if (err.response.statusCode === 403 && (err.response.text || '').includes('ACCOUNT_DELETED')) {
      window.location.href = '/account-deleted';
      return;
    }
    if (err.response.statusCode === 401 && !passive) {
      if (isMsalAuth() && retryFn) {
        try {
          return await retryFn();
        } catch (retryErr: any) {
          TrackJS?.track(`[agent] 401: MSAL retry failed, redirecting to /login: message=${retryErr?.message}, errorCode=${retryErr?.errorCode}`);
          window.location.href = `/login`;
          return;
        }
      }

      TrackJS?.track(`[agent] 401: url=${err.response.req?.url}, authType=${localStorage.getItem('type')}, hasTokens=${hasTokensAvailable()}, body=${(err.response.text || '').substring(0, 200)}`);
      if (isMsalAuth()) {
        getMsalTokens().then(({ idToken, accessToken }) => {
          TrackJS?.track('[agent] 401: MSAL refresh succeeded, reloading');
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('idToken', idToken);
          window.location.reload();
        }).catch((refreshErr) => {
          TrackJS?.track(`[agent] 401: MSAL refresh failed, redirecting to /login: message=${refreshErr?.message}, errorCode=${refreshErr?.errorCode}`);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('tokenExpires');
          window.location.href = `/login`;
        });
      } else {
        TrackJS?.track('[agent] 401: non-MSAL auth, clearing tokens and redirecting to /login');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('tokenExpires');
        window.location.href = `/login`;
      }
      return;
    }
    if (err.response.statusCode === 404 && !passive) {
      window.location.href = '/404';
    }
  }
  if (err && err.message) {
    if (err.message.includes('User not registered')) {
      resetAllLoginStorages();
      if (!passive) {
        window.location.href = `/login?not=reg&t=${Date.now()}`;
      }
    }
    const parsedError = JSON.parse(JSON.stringify(err));
    if (parsedError && parsedError.response) {
      try {
        throw new HttpError(JSON.parse(parsedError.response.text).message, parsedError.response.statusCode);
      } catch (ex) {
        if (parsedError.response.message) {
          throw new Error(parsedError.response.message);
        } else if (parsedError.response.text) {
          throw new Error(parsedError.response.text);
        } else if (parsedError.message) {
          throw new Error(parsedError.message);
        } else if (parsedError.text) {
          throw new Error(parsedError.text);
        } else {
          TrackJS?.track(err);
          throw new Error('Unable to get error from response');
        }
      }
    } else {
      TrackJS?.track(err);
      throw err;
    }
  } else {
    TrackJS?.track(err);
    throw err;
  }
}

async function makeRequest(
  method: 'get' | 'post' | 'put' | 'del',
  url: string,
  body?: any,
  passive = false,
  skipErrorProcess = false,
): Promise<any> {
  const fullUrl = `${API_ROOT}${url}`;

  const executeRequest = async (headers: AuthHeaders) => {
    let req: any;
    if (method === 'del') {
      req = superagent.del(fullUrl);
    } else if (method === 'get') {
      req = superagent.get(fullUrl);
    } else if (method === 'put') {
      req = superagent.put(fullUrl, body);
    } else {
      req = superagent.post(fullUrl, body);
    }

    applyHeaders(req, headers);
    const res = await req;
    return responseBody(res);
  };

  try {
    const headers = await getAuthHeaders();
    return await executeRequest(headers);
  } catch (err: any) {
    if (skipErrorProcess) throw err;

    // MSAL SDK errors (e.g. InteractionRequiredAuthError) have no err.response.
    // Redirect to login rather than letting processError misclassify them.
    if (err.errorCode && !err.response) {
      const isTimeout = err.errorCode === 'timed_out' || err.errorCode === 'monitor_window_timeout';

      if (isTimeout) {
        // Skip page-unload (case 2) and offline (case 3) — benign, not worth logging.
        const isPageUnload = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

        if (!isPageUnload && !isOffline) {
          // Retry once with forceRefresh. If the retry also times out, silent refresh is
          // structurally broken (case 1: 3P cookies blocked, case 4: CSP/X-Frame-Options) —
          // that is the only timed_out variant worth logging.
          try {
            const fresh = await refreshMsalToken();
            return await executeRequest(fresh);
          } catch (retryErr: any) {
            TrackJS?.track(`[agent] silent refresh retry failed after timeout (3P cookies blocked or CSP?): ${String(retryErr?.errorCode ?? retryErr?.message ?? retryErr).slice(0, 200)}`);
          }
        }

        window.location.href = '/login';
        return;
      }

      TrackJS?.track(`[agent] MSAL error: errorCode=${err.errorCode}, message=${err.message}`);
      window.location.href = '/login';
      return;
    }

    const retryFn = isMsalAuth()
      ? async () => {
          getActiveGateway()?.invalidate({ kind: 'session' });
          const freshHeaders = await refreshMsalToken();
          return await executeRequest(freshHeaders);
        }
      : undefined;

    return processError(err, passive, retryFn);
  }
}

const requests = {
  del: (url: string, passive = false) =>
    makeRequest('del', url, undefined, passive),
  get: (url: string, passive = false, skipErrorProcess = false) =>
    makeRequest('get', url, undefined, passive, skipErrorProcess),
  put: (url: string, body?: any, passive = false) =>
    makeRequest('put', url, body, passive),
  post: (url: string, body?: any, passive = false) =>
    makeRequest('post', url, body, passive),
};

export function getErrorMessage(err: any, fallback: string): string {
  try {
    return JSON.parse(err.message).message;
  } catch (e) {
    return fallback;
  }
}

export const agent = {
  requests,
};
